import React, { useState, useEffect } from 'react';
import {
    MdFileDownload,
    MdTrendingUp,
    MdTrendingDown,
    MdPerson,
    MdPeople,
    MdDescription,
    MdCurrencyRupee,
    MdSearch,
    MdArrowBack,
    MdFilterList,
    MdExpandMore,
    MdExpandLess
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import styles from './ProjectSummaryReport.module.css';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import Drawer from '../components/common/Drawer';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { authFetch } from '../components/utils/authFetch';
import { GET_PROJECT_PAYMENT_SUMMARY_API, GET_ALL_PROJECT_API } from '../config/API';

const ProjectSummaryReport = () => {
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    // Stats state
    const [stats, setStats] = useState({
        activeProjects: 0,
        totalVendors: 0,
        openPos: 0,
        totalCommitted: 0
    });

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showStats, setShowStats] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setShowStats(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=${limit}&search=${debouncedSearch}`;

            if (selectedProject?.value) {
                queryParams += `&projectId=${selectedProject.value}`;
            }

            const response = await authFetch(
                `${GET_PROJECT_PAYMENT_SUMMARY_API}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                setReports(result.data || []);
                const pagination = result.pagination;
                setTotalPages(pagination?.totalPages || 1);
                setTotalRecords(pagination?.totalCount || result.data?.length || 0);

                // Update aggregate stats from the first page of data (simple implementation)
                // In a real app, the API might return these stats separately
                if (result.data) {
                    const totalAmt = result.data.reduce((acc, curr) => acc + (parseFloat(curr.totalAmount) || 0), 0);
                    const totalVend = result.data.reduce((acc, curr) => acc + (parseInt(curr.totalVendors) || 0), 0);
                    const totalPOs = result.data.reduce((acc, curr) => acc + (parseInt(curr.totalPOs) || 0), 0);

                    setStats({
                        activeProjects: pagination?.totalCount || result.data.length,
                        totalVendors: totalVend,
                        openPos: totalPOs,
                        totalCommitted: totalAmt
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching project summary report:', error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;
            if (selectedProject?.value) {
                queryParams += `&projectId=${selectedProject.value}`;
            }

            const response = await authFetch(`${GET_PROJECT_PAYMENT_SUMMARY_API}${queryParams}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('An error occurred during export');
        }
    };

    useEffect(() => {
        fetchReports();
    }, [currentPage, debouncedSearch, selectedProject, limit]);

    const getStatusClass = (status) => {
        const s = status?.toUpperCase();
        if (s === 'ACTIVE' || s === 'IN PROGRESS') return styles['status-in-progress'];
        if (s === 'APPROVED' || s === 'COMPLETED') return styles['status-approved'];
        if (s === 'PENDING') return styles['status-pending'];
        if (s === 'ARCHIVED') return styles['status-archived'];
        if (s === 'DELAYED') return styles['status-delayed'];
        return '';
    };

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return `₹${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <div className={styles['purchase-orders-container']}>

            {/* Header Section */}
            <div className={styles['purchase-orders-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div>
                        <h2>Project Summary Report</h2>
                        <p className={styles['page-subtitle']}>Consolidated overview of all active procurement projects and financial commitments.</p>
                    </div>
                </div>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                    <button className={styles['export-btn']} onClick={handleExportCSV}>
                        <MdFileDownload size={18} /> Export CSV
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Project Name</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_PROJECT_API}
                            mapResponse={(p) => ({
                                value: p.id || p._id,
                                label: p.name || p.projectName
                            })}
                            value={selectedProject}
                            onChange={(option) => setSelectedProject(option)}
                            placeholder="Select Project"
                            isClearable
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setSelectedProject(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset Filter
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* Mobile Stats Toggle */}
            {isMobile && (
                <button 
                    className={styles['stats-toggle-btn']}
                    onClick={() => setShowStats(!showStats)}
                >
                    {showStats ? (
                        <>
                            <MdExpandLess /> Hide Summary
                        </>
                    ) : (
                        <>
                            <MdExpandMore /> Show Summary
                        </>
                    )}
                </button>
            )}

            {/* Stats Grid */}
            <div className={`${styles['stats-grid']} ${showStats ? styles['show'] : styles['hide']}`}>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdPerson />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Active Projects</div>
                    <div className={styles['stat-value']}>{stats.activeProjects}</div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['purple']}`}>
                            <MdPeople />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Total Vendors</div>
                    <div className={styles['stat-value']}>{stats.totalVendors}</div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['orange']}`}>
                            <MdDescription />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Open POs</div>
                    <div className={styles['stat-value']}>{stats.openPos}</div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['green']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Total Committed</div>
                    <div className={styles['stat-value']}>{formatCurrency(stats.totalCommitted)}</div>
                </div>
            </div>

            {/* Main Content Table Card */}
            <div className={!isMobile ? styles['card-container-po'] : ''}>
                <div className={styles['search-container']}>
                    <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {!isMobile ? (
                    <div className={styles['table-container']}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Project Name</th>
                                    <th>Status</th>
                                    <th className={styles['text-center']}>Total Vendors</th>
                                    <th className={styles['text-center']}>Total POs</th>
                                    <th className={styles['text-right']}>Total Amount</th>
                                    <th className={styles['text-right']}>Total Paid Amount</th>
                                    <th className={styles['text-right']}>Total Remaining Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <Loader text="Fetching project summary..." />
                                        </td>
                                    </tr>
                                ) : reports.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <NoData title="No Reports Found" message="Try adjusting your search filters." />
                                        </td>
                                    </tr>
                                ) : (
                                    reports.map((project) => (
                                        <tr key={project.projectId}>
                                            <td>
                                                <div className={styles['project-name-cell']}>
                                                    {project.projectName}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`${styles['status-badge']} ${getStatusClass(project.status)}`}>
                                                    {project.status}
                                                </span>
                                            </td>
                                            <td className={styles['text-center']}>
                                                <span
                                                    className={styles['link-text']}
                                                    onClick={() => window.open(`/reports/project/${project.projectId}/vendors?projectName=${encodeURIComponent(project.projectName)}`, '_blank')}
                                                >
                                                    {project.totalVendors}
                                                </span>
                                            </td>
                                            <td className={styles['text-center']}>
                                                <span
                                                    className={styles['link-text']}
                                                    onClick={() => window.open(`/reports/project/${project.projectId}/po-list`, '_blank')}
                                                >
                                                    {project.totalPOs}
                                                </span>
                                            </td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-grandTotal']}`}>{formatCurrency(project.totalAmount)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-paid']}`}>{formatCurrency(project.totalPaidAmount)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-remaining']}`}>{formatCurrency(project.totalRemainingAmount)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles['mobile-card-view']}>
                        {loading ? (
                            <div style={{ padding: '2rem' }}>
                                <Loader text="Fetching reports..." />
                            </div>
                        ) : reports.length === 0 ? (
                            <NoData title="No Reports Found" message="Try adjusting your filters." />
                        ) : (
                            reports.map((project) => (
                                <div key={project.projectId} className={styles['project-card']}>
                                    <div className={styles['card-header']}>
                                        <div className={styles['card-title']}>
                                            <strong>{project.projectName}</strong>
                                            <span className={`${styles['status-badge']} ${getStatusClass(project.status)}`}>
                                                {project.status}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles['card-body']}>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total Vendors:</span>
                                            <span
                                                className={`${styles['card-value']} ${styles['link-text']}`}
                                                onClick={() => window.open(`/reports/project/${project.projectId}/vendors?projectName=${encodeURIComponent(project.projectName)}`, '_blank')}
                                            >
                                                {project.totalVendors}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total POs:</span>
                                            <span
                                                className={`${styles['card-value']} ${styles['link-text']}`}
                                                onClick={() => window.open(`/reports/project/${project.projectId}/po-list`, '_blank')}
                                            >
                                                {project.totalPOs}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total Amount:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-grandTotal']}`}>
                                                {formatCurrency(project.totalAmount)}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Paid Amount:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-paid']}`}>
                                                {formatCurrency(project.totalPaidAmount)}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Remaining:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-remaining']}`}>
                                                {formatCurrency(project.totalRemainingAmount)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <Pagination limit={limit} onLimitChange={handleLimitChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPageChange={(page) => setCurrentPage(page)}
                    currentCount={reports.length}
                    entityName="Project Summary"
                />
            </div>


        </div>
    );
};

export default ProjectSummaryReport;
